import React, { useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  applyOpeningBalanceSheet,
  downloadOpeningBalanceTemplate,
  uploadOpeningBalanceSheet,
  type OpeningBalanceSheetIssue,
  type OpeningBalanceSheetPreviewDTO,
} from "../../data/openingBalancesApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * Opening balances in bulk: download the grid, fill it in, upload, review, apply.
 *
 * FOR THE CLIENT WITH THREE HUNDRED LEDGERS. The grid below is the right tool
 * for a dozen figures and the wrong one for a trial balance printed off a legacy
 * system — and because Continue only unlocks when debits equal credits, one
 * mistyped cell means hunting the difference by eye. This is the same work in a
 * spreadsheet, where a CA can paste a column and let Excel add it up.
 *
 * NOTHING HERE REACHES ZOHO. Download reads, upload parses and answers, apply
 * stages locally. The entry still goes to the client's books from the screen's
 * own Send button, behind the balance check, exactly as a typed figure does.
 *
 * NO STAGING TABLE, unlike the ledger and contact imports. Those keep every
 * parsed row in the database because their push runs across many requests and
 * has to resume after a failure. Applying here is a handful of local writes with
 * nothing to resume, so the review lives in this component and goes back to the
 * server on confirm. A CA who closes the tab mid-review leaves nothing behind.
 */
export function OpeningBalanceImportPanel({
  clientId,
  clientName,
  onApplied,
}: {
  clientId: ClientId;
  clientName: string;
  /** Bump the grid's reload token: the figures it holds have just changed. */
  onApplied: () => void;
}) {
  const [phase, setPhase] = useState<"idle" | "uploading" | "review" | "applying">("idle");
  const [review, setReview] = useState<OpeningBalanceSheetPreviewDTO | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const busy = phase === "uploading" || phase === "applying";

  async function download() {
    setError(null);

    try {
      await downloadOpeningBalanceTemplate(clientId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not build that sheet.");
    }
  }

  async function upload(file: File) {
    setPhase("uploading");
    setError(null);
    setApplied(null);
    setFilename(file.name);

    try {
      const next = await uploadOpeningBalanceSheet(clientId, file);
      setReview(next);
      setPhase("review");
    } catch (caught) {
      setReview(null);
      setPhase("idle");
      setError(caught instanceof Error ? caught.message : "Could not read that sheet.");
    } finally {
      // Otherwise choosing the same file twice in a row does nothing: the input
      // holds the previous value and fires no change event.
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function apply() {
    if (review === null) return;

    setPhase("applying");
    setError(null);

    try {
      await applyOpeningBalanceSheet(clientId, review.rows);
      const { to_apply: changed, to_clear: cleared } = review.counts;
      setApplied(
        cleared > 0
          ? `${describe(changed, "figure")} applied and ${cleared} cleared.`
          : `${describe(changed, "figure")} applied.`
      );
      setReview(null);
      setFilename(null);
      setPhase("idle");
      // The grid re-reads from the server rather than being told what changed:
      // it is the thing that owns what is staged, and a second copy of that
      // answer is a second thing to keep true.
      onApplied();
    } catch (caught) {
      setPhase("review");
      setError(caught instanceof Error ? caught.message : "Could not apply those figures.");
    }
  }

  function discard() {
    setReview(null);
    setFilename(null);
    setError(null);
    setPhase("idle");
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) void upload(file);
  }

  return (
    <section className="flex flex-col gap-4 rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-medium">Bulk Upload Ledger Balances</h4>
          <p className="text-sm text-muted-foreground">
            To update multiple ledger balances at once, download the ledger balance sheet below, enter the
            balances, and upload the completed sheet back. The uploaded values will update the ledger balances on
            this page.
          </p>
        </div>
        <CoButton onClick={() => void download()} disabled={busy}>
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}
      {applied !== null && <Callout variant="info">{applied}</Callout>}

      {review === null ? (
        /*
          A label wrapping a hidden input rather than a button: keyboard and
          screen-reader behaviour for free, which a div with an onClick is not.

          `relative` is load-bearing, not styling. The input is `sr-only`, which
          is `position: absolute`, and without a positioned ancestor it resolves
          against the BODY — escaping the step's scroll container and stretching
          the page. Same note as the three sibling panels, same afternoon lost.
        */
        <label
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
            dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
            phase === "uploading" && "pointer-events-none opacity-70"
          )}
        >
          <UploadIcon className="size-5 text-muted-foreground" />
          <span className="text-sm font-medium">
            {phase === "uploading" ? "Reading the sheet…" : "Drop the filled-in sheet here, or click to choose"}
          </span>
          <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 2 MB</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="sr-only"
            disabled={phase === "uploading"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </label>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{filename}</span>
            <span className="text-xs text-muted-foreground">
              {review.counts.rows_read} {review.counts.rows_read === 1 ? "row" : "rows"} read
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Count label="To apply" value={review.counts.to_apply} tone="apply" />
            <Count label="To clear" value={review.counts.to_clear} tone="clear" />
            <Count label="Unchanged" value={review.counts.unchanged} tone="quiet" />
            <Count label="Skipped" value={review.counts.skipped} tone="quiet" />
            <Count label="Refused" value={review.counts.refused} tone="refused" />
          </div>

          {/* The totals the grid WOULD show, so the sheet can be checked before
              it lands rather than after. Locked receivable and payable are in
              them, the same as the grid's own Total.

              One block per GST registration once there is more than one: Zoho
              balances each location separately, so a sheet that only balances
              in total is a sheet the push will still refuse. */}
          {(review.totals_by_registration ?? []).length > 1 ? (
            <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-3 text-sm">
              <span className="font-medium">Totals after applying</span>
              {review.totals_by_registration.map((registration) => (
                <div key={registration.location_id ?? "primary"} className="flex flex-col gap-0.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>{registration.name}</span>
                    <span className="tabular-nums">
                      {money(registration.totals.debit)} Dr · {money(registration.totals.credit)} Cr
                    </span>
                  </div>
                  {registration.totals.balanced ? (
                    <span className="text-xs text-emerald-700 dark:text-emerald-400">
                      Debits and credits agree.
                    </span>
                  ) : (
                    <span className="text-xs text-amber-800 dark:text-amber-400">
                      Difference {money(Math.abs(registration.totals.difference))} — this registration has to
                      agree on its own before the entry can be sent.
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-1 rounded-md border bg-muted/20 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">Totals after applying</span>
                <span className="tabular-nums">
                  {money(review.totals.debit)} Dr · {money(review.totals.credit)} Cr
                </span>
              </div>
              {review.totals.balanced ? (
                <span className="text-emerald-700 dark:text-emerald-400">Debits and credits agree.</span>
              ) : (
                <span className="text-amber-800 dark:text-amber-400">
                  Difference {money(Math.abs(review.totals.difference))}. They must agree before the entry
                  can be sent — you can apply this sheet and fix the rest in the grid.
                </span>
              )}
            </div>
          )}

          {review.skipped.length > 0 && (
            <Issues
              title="Skipped"
              blurb="Receivable and payable are set by the outstanding documents, so figures typed against them were left out. Everything else on those rows is fine."
              issues={review.skipped}
              tone="info"
            />
          )}

          {review.refused.length > 0 && (
            <Issues
              title="Refused"
              blurb="These rows are not applied. Every other row still is, and a refused account keeps the figure it already had."
              issues={review.refused}
              tone="warning"
            />
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Applying replaces the figures on the accounts in this sheet. Nothing is sent to Zoho.
            </p>
            <div className="flex gap-2">
              <CoButton variant="outline" onClick={discard} disabled={busy}>
                Cancel
              </CoButton>
              <CoButton
                onClick={() => void apply()}
                disabled={busy || review.counts.to_apply + review.counts.to_clear === 0}
              >
                {phase === "applying" ? "Applying…" : "Apply to grid"}
              </CoButton>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** One count. Quiet unless it is something to act on. */
function Count({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "apply" | "clear" | "quiet" | "refused";
}) {
  const live = value > 0;

  return (
    <span
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs font-medium",
        !live && "bg-muted/40 text-muted-foreground",
        live && tone === "apply" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        live && tone === "clear" && "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
        live && tone === "refused" && "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
        live && tone === "quiet" && "bg-muted/40 text-muted-foreground"
      )}
    >
      {label} {value}
    </span>
  );
}

/**
 * A refusal list, by sheet row.
 *
 * The row number is the point: a CA fixes this in Excel, and "row 47" is the
 * only thing that gets them to the cell.
 */
function Issues({
  title,
  blurb,
  issues,
  tone,
}: {
  title: string;
  blurb: string;
  issues: OpeningBalanceSheetIssue[];
  tone: "info" | "warning";
}) {
  return (
    <div className="flex flex-col gap-2">
      <Callout variant={tone}>
        <span className="font-medium">{title}</span> — {blurb}
      </Callout>
      <ul className="flex flex-col gap-1">
        {issues.map((issue) => (
          <li
            key={`${issue.sheet_row_no}-${issue.account}`}
            className="flex flex-wrap items-baseline gap-x-2 text-sm"
          >
            <span className="shrink-0 tabular-nums text-muted-foreground">Row {issue.sheet_row_no}</span>
            <span className="font-medium">{issue.account}</span>
            <span className="text-muted-foreground">{issue.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Indian grouping, the same reading as the grid's figures. */
function money(value: number): string {
  return value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function describe(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : noun + "s"}`;
}
